import { SyncableRecord } from "./syncable-record";
import { DietaryTag } from "./dietary";

export enum RecipeDifficulty {
  Easy = "Easy",
  Medium = "Medium",
  Hard = "Hard",
}

// Difficulty value → i18n key (display only; the stored value is the identifier).
export const DIFFICULTY_LABEL_KEYS: Record<RecipeDifficulty, string> = {
  [RecipeDifficulty.Easy]: "labels.difficulty.easy",
  [RecipeDifficulty.Medium]: "labels.difficulty.medium",
  [RecipeDifficulty.Hard]: "labels.difficulty.hard",
};

export interface Recipe extends SyncableRecord {
  title: string;
  description?: string; // short free-text summary shown under the title
  source?: string; // free-text attribution (cookbook + page, magazine, person…)
  sourceUrl?: string;
  image: string; // remote (Supabase Storage) URL — synced; set only after upload
  localImageUri?: string; // device-local file path — NOT synced; for offline display
  imagePendingUpload?: boolean; // NOT synced; localImageUri awaits upload to Storage
  cachedImageUri?: string; // NOT synced; local cache of the remote `image` for offline display
  cookingDuration?: number;
  // null/undefined = N/A (hidden). Store null when the user clears it so the sync
  // push sends an explicit null (undefined would be dropped from the payload,
  // leaving the server value unchanged / applying the servings default).
  difficulty?: RecipeDifficulty | null;
  servings?: number | null;
  // Personal 1-5 star rating. null/undefined = unrated (N/A) — store null when
  // cleared so the sync push sends an explicit null (see the difficulty note).
  rating?: number | null;
  favourite: boolean;
  notes?: string;
  cooked: boolean;
  dietary?: DietaryTag[]; // dietary suitability tags (synced as jsonb)
  tags?: string[]; // free-text tags (synced as jsonb, mirrors dietary)
  // When the recipe's *content* was last edited via the edit form. Distinct from
  // updatedAt (server-stamped on any write, incl. favourite/rating/cooked). Set
  // only by the edit-save handlers; absent until the recipe is first edited.
  contentEditedAt?: string; // ISO timestamp
}

export interface RecentlyViewedRecipe extends Recipe {
  viewedAt: string;
}

export function createEmptyRecipe(overrides: Partial<Recipe> = {}): Recipe {
  return {
    id: "",
    title: "",
    description: "",
    source: "",
    sourceUrl: "",
    image: "",
    difficulty: RecipeDifficulty.Easy,
    servings: 1,
    cookingDuration: undefined,
    rating: null,
    favourite: false,
    notes: "",
    cooked: false,
    dietary: [],
    tags: [],
    ...overrides,
  };
}
